import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result, Typography } from 'antd';

interface ErrorBoundaryProps {
  /** Зміна ключа (напр. перехід на іншу сторінку) скидає стан помилки */
  resetKey: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  mdError: Error | null;
}

/**
 * Перехоплює помилку рендеру сторінки. Без нього будь-який TypeError на одній
 * сторінці розмонтовує весь застосунок — користувач бачить білий екран і
 * втрачає меню. З ним падає лише вміст сторінки, а навігація лишається робочою.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { mdError: null };

  static getDerivedStateFromError(mdError: Error): ErrorBoundaryState {
    return { mdError };
  }

  componentDidCatch(mdError: Error, mdInfo: ErrorInfo): void {
    // Стек компонентів — для діагностики в консолі браузера (F12)
    console.error('Помилка на сторінці:', mdError, mdInfo.componentStack);
  }

  componentDidUpdate(mdPrevProps: ErrorBoundaryProps): void {
    if (this.state.mdError && mdPrevProps.resetKey !== this.props.resetKey) {
      this.setState({ mdError: null });
    }
  }

  private mdHandleRetry = (): void => {
    this.setState({ mdError: null });
  };

  render(): ReactNode {
    const { mdError } = this.state;
    if (!mdError) {
      return this.props.children;
    }

    return (
      <Result
        status="error"
        title="Сторінку не вдалося відкрити"
        subTitle="У цьому розділі сталася помилка. Решта системи працює — спробуйте ще раз або оберіть інший розділ у меню."
        extra={[
          <Button key="retry" type="primary" onClick={this.mdHandleRetry}>
            Спробувати ще раз
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>
            Перезавантажити сторінку
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Технічні деталі для підтримки:
        </Typography.Paragraph>
        <Typography.Paragraph code copyable style={{ marginBottom: 0 }}>
          {mdError.message || String(mdError)}
        </Typography.Paragraph>
      </Result>
    );
  }
}
